// src/pages/AdminTodoConfig.jsx
import React, { useEffect, useState } from 'react';
import PageLayout from '../components/PageLayout';
import Button from '../components/Button';
import toast from 'react-hot-toast';
import { CirclePlus, Save } from 'lucide-react';
import { getTodoConfig, setTodoConfig } from '../api/todos';

const CHAMPS = [
  { key: 'nom', label: 'Nom' },
  { key: 'prenom', label: 'Prénom' },
  { key: 'date_naissance', label: 'Date de naissance' },
  { key: 'sexe', label: 'H/F' },
  { key: 'champ_libre', label: 'Champ libre' },
];

const NATURE_FILTRES = [
  { key: 'tous', label: 'Toutes' },
  { key: 'actives', label: 'Actives' },
  { key: 'inactives', label: 'Inactives' },
];

function slugify(s) {
  return String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export default function AdminTodoConfig() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [natures, setNatures] = useState([]);
  const [champs, setChamps] = useState({});
  const [newNature, setNewNature] = useState('');
  const [natureFilter, setNatureFilter] = useState('tous');

  const refresh = async () => {
    setLoading(true);
    setError('');
    try {
      const cfg = await getTodoConfig();
      setNatures(cfg.natures || []);
      setChamps(cfg.champs || {});
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const activeCount = natures.filter((n) => n.actif).length;
  const visibleNatures = natures.filter((n) =>
    natureFilter === 'actives' ? n.actif : natureFilter === 'inactives' ? !n.actif : true,
  );

  const toggleNature = (code) => {
    setNatures((prev) => {
      const n = prev.find((x) => x.code === code);
      if (n?.actif && activeCount <= 1) {
        toast.error('Au moins une nature doit rester active');
        return prev;
      }
      return prev.map((x) => (x.code === code ? { ...x, actif: !x.actif } : x));
    });
  };

  const renameNature = (code, libelle) => {
    setNatures((prev) => prev.map((x) => (x.code === code ? { ...x, libelle } : x)));
  };

  const addNature = () => {
    const libelle = newNature.trim();
    if (!libelle) return;
    const code = slugify(libelle);
    if (!code) {
      toast.error('Libellé invalide');
      return;
    }
    if (natures.some((x) => x.code === code)) {
      toast.error('Cette nature existe déjà');
      return;
    }
    setNatures((prev) => [...prev, { code, libelle, actif: true }]);
    setNewNature('');
    setNatureFilter('tous'); // la nouvelle nature (active) doit rester visible quel que soit le filtre courant
  };

  const setChamp = (key, prop, val) => {
    setChamps((prev) => {
      const cur = { ...(prev[key] || { visible: true, obligatoire: false }), [prop]: val };
      if (prop === 'visible' && !val) cur.obligatoire = false; // masqué => non obligatoire
      return { ...prev, [key]: cur };
    });
  };

  const onSave = async () => {
    setSaving(true);
    try {
      await setTodoConfig({ natures, champs });
      toast.success('Configuration enregistrée');
      await refresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout title="Configuration des To Do">
      <div className="max-w-2xl mx-auto space-y-8">
        <h1 className="text-xl font-bold text-[#B3000A]">Configuration des ToDo</h1>
        {error && <div className="text-red-600">{error}</div>}
        {loading ? (
          <div>Chargement…</div>
        ) : (
          <>
            <section>
              <div className="flex items-center justify-between gap-3 mb-3">
                <h2 className="font-bold">Natures</h2>
                <div className="inline-flex rounded-md border overflow-hidden text-sm">
                  {NATURE_FILTRES.map((f) => (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => setNatureFilter(f.key)}
                      className={
                        natureFilter === f.key
                          ? 'px-3 py-1 bg-[#A51A1F] text-white'
                          : 'px-3 py-1 bg-white text-slate-600 hover:bg-slate-50'
                      }
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-xs text-slate-500 mb-2">
                {activeCount} active(s) / {natures.length} au total
              </p>
              <div className="space-y-2">
                {visibleNatures.length === 0 ? (
                  <p className="text-sm text-slate-400 italic">
                    {natureFilter === 'tous'
                      ? 'Aucune nature.'
                      : `Aucune nature ${natureFilter === 'actives' ? 'active' : 'inactive'}.`}
                  </p>
                ) : (
                  visibleNatures.map((n) => (
                    <div
                      key={n.code}
                      className="flex items-center gap-3 bg-white border rounded-md px-3 py-2"
                    >
                      <input
                        className="flex-1 border rounded px-2 py-1"
                        value={n.libelle}
                        onChange={(e) => renameNature(n.code, e.target.value)}
                      />
                      <label className="inline-flex items-center gap-2 text-sm whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={!!n.actif}
                          onChange={() => toggleNature(n.code)}
                        />
                        Active
                      </label>
                    </div>
                  ))
                )}
              </div>
              <div className="flex items-center gap-2 mt-3">
                <input
                  className="flex-1 border rounded px-2 py-1"
                  placeholder="Nouvelle nature…"
                  value={newNature}
                  onChange={(e) => setNewNature(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addNature();
                    }
                  }}
                />
                <Button onClick={addNature} className="inline-flex items-center gap-1">
                  <CirclePlus size={16} /> Ajouter
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Une nature ne se supprime pas (désactivez-la). Au moins une doit rester active.
              </p>
            </section>

            <section>
              <h2 className="font-bold mb-3">Champs du formulaire de création</h2>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500">
                    <th className="py-1">Champ</th>
                    <th className="py-1">Visible</th>
                    <th className="py-1">Obligatoire</th>
                  </tr>
                </thead>
                <tbody>
                  {CHAMPS.map(({ key, label }) => {
                    const c = champs[key] || { visible: true, obligatoire: false };
                    return (
                      <tr key={key} className="border-t">
                        <td className="py-2">{label}</td>
                        <td>
                          <input
                            type="checkbox"
                            checked={!!c.visible}
                            onChange={(e) => setChamp(key, 'visible', e.target.checked)}
                          />
                        </td>
                        <td>
                          <input
                            type="checkbox"
                            checked={!!c.obligatoire}
                            disabled={!c.visible}
                            onChange={(e) => setChamp(key, 'obligatoire', e.target.checked)}
                          />
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="border-t text-slate-400">
                    <td className="py-2">N° de tâche (= id)</td>
                    <td>
                      <input type="checkbox" checked disabled readOnly />
                    </td>
                    <td>
                      <input type="checkbox" checked disabled readOnly />
                    </td>
                  </tr>
                </tbody>
              </table>
              <p className="text-xs text-slate-500 mt-1">
                Le n° de tâche est toujours présent et non configurable.
              </p>
            </section>

            <Button onClick={onSave} disabled={saving} className="inline-flex items-center gap-1">
              <Save size={16} /> {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </>
        )}
      </div>
    </PageLayout>
  );
}
